import {
  acceptsEnvelope,
  boundedJson,
  documentScope,
  envelope,
  RUNTIME_LIMITS,
  validOperation,
} from '../../contracts/runtime-v2.mjs';
import {
  presentSdkFailure,
  sdkErrorReply,
  sdkFailureMessage,
  readSdkErrorReply,
} from './tier0/error-presentation.mjs';

// peer is the current frame WindowProxy (host) or parent (child), never looked
// up from event.source. Handlers are trusted composition registrations.
export function createRuntimeTransport({
  window,
  peer,
  scope: input,
  side,
  handlers = {},
  limits = {},
}) {
  const scope = documentScope(input);
  handlers = { ...handlers };
  if (!['host', 'child'].includes(side) || !peer || peer === window)
    throw new TypeError('invalid_peer');
  const targetOrigin = side === 'host' ? scope.appOrigin : scope.hostOrigin;
  const requestIdPattern = side === 'host' ? /^c[1-9][0-9]{0,15}$/ : /^h[1-9][0-9]{0,15}$/;
  const pending = new Map();
  const outgoingSubscriptions = new Map();
  const incoming = new Map();
  let receivedSequence = 0;
  const invalidations = new Set();
  const bounds = { ...RUNTIME_LIMITS, ...limits };
  let active = true;
  let sequence = 0;
  let epoch = Date.now();
  let received = 0;
  const send = (type, id, operation, payload) => {
    if (active) peer.postMessage(envelope(scope, type, id, operation, payload), targetOrigin);
  };
  const sendControl = (type, id, operation) => {
    try {
      send(type, id, operation, {});
    } catch {
      // A detached peer must not prevent local cancellation and cleanup.
    }
  };
  const settle = (id, error, result) => {
    const entry = pending.get(id);
    if (!entry) return;
    pending.delete(id);
    clearTimeout(entry.timer);
    entry.signal?.removeEventListener('abort', entry.abort);
    error ? entry.reject(error) : entry.resolve(result);
  };
  const stopIncoming = (id) => {
    const call = incoming.get(id);
    if (!call) return;
    incoming.delete(id);
    call.controller.abort();
    try {
      call.dispose?.();
    } catch {
      /* cleanup is best effort; authority already retired */
    }
  };
  const invalidate = (notify = true) => {
    if (!active) return;
    if (notify) sendControl('invalidate', `i${++sequence}`, 'runtime.invalidate');
    active = false;
    window.removeEventListener('message', receive);
    for (const id of pending.keys()) settle(id, new Error('scope_invalidated'));
    for (const id of incoming.keys()) stopIncoming(id);
    outgoingSubscriptions.clear();
    for (const callback of invalidations) {
      try {
        callback();
      } catch {
        /* isolate consumers */
      }
    }
    invalidations.clear();
  };
  const start = (type, operation, args, { signal } = {}) => {
    if (!active) return Promise.reject(new Error('scope_invalidated'));
    if (!validOperation(operation)) return Promise.reject(new TypeError('invalid_operation'));
    if (signal?.aborted) return Promise.reject(new Error('request_cancelled'));
    const liveIds = new Set([...pending.keys(), ...outgoingSubscriptions.keys()]);
    // subscribe installs its observer before starting so initial events cannot
    // race the acknowledgement. Count that operation once, along with every
    // other pending request or live subscription.
    if (type === 'subscribe') liveIds.delete(`${side === 'host' ? 'h' : 'c'}${sequence + 1}`);
    if (liveIds.size >= bounds.pending) return Promise.reject(new Error('request_limit'));
    const payload = boundedJson(args);
    const id = `${side === 'host' ? 'h' : 'c'}${++sequence}`;
    return new Promise((resolve, reject) => {
      const abort = () => {
        sendControl('cancel', id, operation);
        outgoingSubscriptions.delete(id);
        settle(id, new Error('request_cancelled'));
      };
      const timer = setTimeout(abort, bounds.timeoutMs);
      pending.set(id, {
        resolve: (value) => resolve({ id, value }),
        reject,
        timer,
        signal,
        abort,
        operation,
      });
      signal?.addEventListener('abort', abort, { once: true });
      try {
        send(type, id, operation, payload);
      } catch (error) {
        settle(id, error);
      }
    });
  };
  async function dispatchSubscription(handler, message, call) {
    if (typeof handler.subscribe !== 'function') throw new Error('operation_denied');
    const { controller } = call;
    const dispose = await handler.subscribe(
      message.payload,
      (event) => {
        if (incoming.get(message.requestId) === call && !controller.signal.aborted)
          send('event', message.requestId, message.operation, event);
      },
      { signal: controller.signal },
    );
    if (typeof dispose !== 'function') throw new Error('invalid_subscription');
    if (controller.signal.aborted || !active) {
      dispose();
      return;
    }
    call.dispose = dispose;
    send('reply', message.requestId, message.operation, { ok: true, result: null });
  }
  async function dispatchRequest(handler, message, call) {
    if (typeof handler.request !== 'function') throw new Error('operation_denied');
    const result = await handler.request(message.payload, { signal: call.controller.signal });
    if (!call.controller.signal.aborted)
      send('reply', message.requestId, message.operation, {
        ok: true,
        result,
        ...(sdkFailureMessage(result) ? { message: sdkFailureMessage(result) } : {}),
      });
    incoming.delete(message.requestId);
  }
  async function dispatch(message) {
    const handler = handlers[message.operation];
    if (!Object.hasOwn(handlers, message.operation) || !handler) return;
    if (incoming.size >= bounds.pending) {
      invalidate();
      return;
    }
    const call = { controller: new AbortController(), dispose: null };
    incoming.set(message.requestId, call);
    try {
      if (message.type === 'subscribe') await dispatchSubscription(handler, message, call);
      else await dispatchRequest(handler, message, call);
    } catch (error) {
      stopIncoming(message.requestId);
      send('reply', message.requestId, message.operation, { ok: false, ...sdkErrorReply(error) });
    }
  }
  function controlMessage(message) {
    if (message.type === 'invalidate') {
      invalidate(false);
      return true;
    }
    if (['cancel', 'unsubscribe'].includes(message.type)) {
      stopIncoming(message.requestId);
      return true;
    }
    return false;
  }
  function responseMessage(message) {
    if (message.type === 'event') {
      const subscription = outgoingSubscriptions.get(message.requestId);
      if (subscription?.operation === message.operation) {
        try {
          subscription.onEvent(boundedJson(message.payload));
        } catch {
          /* consumer owns rendering */
        }
      }
      return true;
    }
    if (message.type !== 'reply') return false;
    const entry = pending.get(message.requestId);
    if (entry?.operation !== message.operation) return true;
    message.payload?.ok === true
      ? settle(
          message.requestId,
          null,
          presentSdkFailure(message.payload.result, message.payload.message),
        )
      : settle(message.requestId, readSdkErrorReply(message.payload));
    return true;
  }
  function acceptsNewCall(id) {
    if (!requestIdPattern.test(id)) return false;
    const incomingSequence = Number(id.slice(1));
    if (!Number.isSafeInteger(incomingSequence) || incomingSequence <= receivedSequence)
      return false;
    receivedSequence = incomingSequence;
    return true;
  }
  function boundMessage(event) {
    return (
      active &&
      event.source === peer &&
      event.origin === targetOrigin &&
      acceptsEnvelope(event.data, scope)
    );
  }
  function receive(event) {
    if (!boundMessage(event)) return;
    const now = Date.now();
    if (now - epoch >= 1000) {
      epoch = now;
      received = 0;
    }
    if (++received > bounds.messagesPerSecond) {
      invalidate();
      return;
    }
    const message = event.data;
    if (controlMessage(message) || responseMessage(message)) return;
    // Per-peer request IDs are monotonic. Earlier active IDs remain valid for
    // replies/cancellation; completed traffic does not consume a lifetime set.
    if (!acceptsNewCall(message.requestId)) return;
    if (!Object.hasOwn(handlers, message.operation)) {
      send('reply', message.requestId, message.operation, { ok: false, error: 'operation_denied' });
      return;
    }
    void dispatch(message);
  }
  window.addEventListener('message', receive);
  return Object.freeze({
    registerHandlers(additions) {
      if (!active) throw new Error('scope_invalidated');
      for (const [operation, handler] of Object.entries(additions)) {
        if (
          !validOperation(operation) ||
          Object.hasOwn(handlers, operation) ||
          !handler ||
          !['request', 'subscribe'].some((name) => typeof handler[name] === 'function')
        )
          throw new TypeError('invalid_handler');
      }
      Object.assign(handlers, additions);
    },
    request: async (operation, args, options) =>
      (await start('request', operation, args, options)).value,
    async subscribe(operation, args, onEvent) {
      if (typeof onEvent !== 'function') throw new TypeError('callback_required');
      // Install before the reply: a provider may emit its initial snapshot
      // synchronously while opening the subscription.
      const id = `${side === 'host' ? 'h' : 'c'}${sequence + 1}`;
      outgoingSubscriptions.set(id, { operation, onEvent });
      try {
        await start('subscribe', operation, args);
      } catch (error) {
        outgoingSubscriptions.delete(id);
        throw error;
      }
      return () => {
        outgoingSubscriptions.delete(id);
        sendControl('unsubscribe', id, operation);
      };
    },
    onInvalidate(callback) {
      if (typeof callback !== 'function') throw new TypeError('callback_required');
      if (!active) {
        callback();
        return () => {};
      }
      invalidations.add(callback);
      return () => invalidations.delete(callback);
    },
    invalidate,
  });
}
